import * as THREE from 'three';
import { ft } from '../core/units.js';

/**
 * Draws whatever big thing is currently out there.
 *
 * Only the big ones. The small ones are entities and `entityView` has been
 * drawing them since step three without being told anything has changed, which
 * is what "catchable therefore throwable" looks like from the renderer's side:
 * a scorpion draws the same way an orange does because it is the same thing.
 *
 * A box on legs and a wedge for a snout, in the kind's own colour. Two groups
 * can be about at once and a herd is up to five, so the pool is ten.
 */
const POOL = 10;

export function createWildlifeView({ wildlife }) {
  const group = new THREE.Group();
  group.name = 'wildlife';

  const disposables = [];
  const keep = (thing) => {
    disposables.push(thing);
    return thing;
  };

  const boxGeometry = keep(new THREE.BoxGeometry(1, 1, 1));
  const snoutGeometry = keep(new THREE.ConeGeometry(0.5, 1, 4));
  const materials = new Map();

  function materialFor(colour) {
    let found = materials.get(colour);
    if (!found) {
      found = keep(new THREE.MeshStandardMaterial({ color: colour, roughness: 0.95 }));
      materials.set(colour, found);
    }
    return found;
  }

  const slots = [];
  for (let i = 0; i < POOL; i += 1) {
    const body = new THREE.Mesh(boxGeometry, materialFor(0x000000));
    const snout = new THREE.Mesh(snoutGeometry, materialFor(0x000000));
    snout.rotation.x = Math.PI / 2;
    body.add(snout);
    body.visible = false;
    group.add(body);
    slots.push({ body, snout });
  }

  return {
    object3D: group,

    sync() {
      const about = wildlife.creatures;
      for (let i = 0; i < slots.length; i += 1) {
        const slot = slots[i];
        const creature = about[i];
        if (!creature) {
          slot.body.visible = false;
          continue;
        }
        const definition = creature.definition;
        const width = ft(definition.radius) * 2;
        const height = ft(definition.height);
        slot.body.visible = true;
        slot.body.scale.set(width, height * 0.6, width * 1.4);
        slot.body.position.set(creature.position.x, creature.position.y + height * 0.3, creature.position.z);
        slot.body.rotation.y = creature.yaw;
        slot.body.material = materialFor(definition.colour);
        slot.snout.material = slot.body.material;
        // Scaled in the body's local space, so it stays a snout whatever the
        // body is.
        slot.snout.scale.set(0.7, 0.6, 0.7);
        slot.snout.position.set(0, -0.1, 0.8);
      }
    },

    dispose() {
      for (const thing of disposables) thing.dispose?.();
      group.removeFromParent();
    },
  };
}
